import { keepPreviousData, queryOptions } from '@tanstack/react-query';
import { allocationService } from './services/allocationService';
import type { AllocationBody } from './types/Allocation';
import type { AllocationDefaultsParams } from './types/AllocationDefaults';
import type { CheckProfessionalOption, PoolFilters, UnallocatedFilters } from './types/Checks';
import type {
  AllocationFilters,
  ContractSearchParams,
  PaginatedListResponse,
} from './types/Filters';
import type { PercentageCheckFilters } from './types/PercentageCheck';
import type { SummaryFilters } from './types/Summary';
import type { TimelineFilters } from './types/Timeline';
import { sleepUnlessAborted } from './utils/async';

const SEARCH_DEBOUNCE_MS = 300;
const VALIDATION_DEBOUNCE_MS = 400;
/** Página máxima do back: o select abre com a consulta inteira (algumas dezenas de linhas). */
const CHECK_PROFESSIONALS_PER_PAGE = 100;

/** Uma opção por profissional (há quem tenha várias linhas), na ordem das linhas. */
function toProfessionalOptions(response: {
  items: { professionalId: number; registrationNumber: number | null; professionalName: string }[];
  total: number;
  perPage: number;
}): PaginatedListResponse<CheckProfessionalOption> {
  const byId = new Map<number, CheckProfessionalOption>();
  for (const row of response.items) {
    if (!byId.has(row.professionalId)) {
      byId.set(row.professionalId, {
        id: row.professionalId,
        registrationNumber: row.registrationNumber,
        name: row.professionalName,
      });
    }
  }
  const items = [...byId.values()];
  // Página incompleta = lista inteira: o total é o de profissionais, não o de linhas.
  const complete = response.total <= response.perPage;
  return {
    items,
    total: complete ? items.length : response.total,
    page: 1,
    perPage: response.perPage,
  };
}

export const allocationQueries = {
  all: () => ['allocation'] as const,

  lists: () => [...allocationQueries.all(), 'list'] as const,
  list: (filters: AllocationFilters) =>
    queryOptions({
      queryKey: [...allocationQueries.lists(), filters] as const,
      queryFn: ({ signal }) => allocationService.list(filters, signal),
      placeholderData: keepPreviousData,
    }),

  timelines: () => [...allocationQueries.all(), 'timeline'] as const,
  timeline: (filters: TimelineFilters) =>
    queryOptions({
      queryKey: [...allocationQueries.timelines(), filters] as const,
      queryFn: ({ signal }) => allocationService.timeline(filters, signal),
      placeholderData: keepPreviousData,
    }),

  checks: () => [...allocationQueries.all(), 'checks'] as const,
  pool: (filters: PoolFilters) =>
    queryOptions({
      queryKey: [...allocationQueries.checks(), 'pool', filters] as const,
      queryFn: ({ signal }) => allocationService.listPool(filters, signal),
      placeholderData: keepPreviousData,
    }),
  unallocated: (filters: UnallocatedFilters) =>
    queryOptions({
      queryKey: [...allocationQueries.checks(), 'unallocated', filters] as const,
      queryFn: ({ signal }) => allocationService.listUnallocated(filters, signal),
      placeholderData: keepPreviousData,
    }),
  summary: (filters: SummaryFilters) =>
    queryOptions({
      queryKey: [...allocationQueries.checks(), 'summary', filters] as const,
      queryFn: ({ signal }) => allocationService.listSummary(filters, signal),
      placeholderData: keepPreviousData,
    }),
  percentageCheck: (filters: PercentageCheckFilters) =>
    queryOptions({
      queryKey: [...allocationQueries.checks(), 'percentageCheck', filters] as const,
      queryFn: ({ signal }) => allocationService.listPercentageCheck(filters, signal),
      placeholderData: keepPreviousData,
    }),

  /**
   * Busca na própria consulta, na mesma data: oferece só quem aparece nela,
   * inclusive contrato "Em contratação", que `GET /professionals` não traz.
   */
  checkProfessionals: (check: 'pool' | 'unallocated', search: string, date: string | undefined) =>
    queryOptions({
      queryKey: [...allocationQueries.checks(), 'professionals', check, search, date] as const,
      queryFn: async ({ signal }): Promise<PaginatedListResponse<CheckProfessionalOption>> => {
        await sleepUnlessAborted(SEARCH_DEBOUNCE_MS, signal);
        const params = {
          date,
          professionalSearch: search.trim() || undefined,
          sortBy: 'professional',
          page: 1,
          perPage: CHECK_PROFESSIONALS_PER_PAGE,
        } as const;
        const response =
          check === 'pool'
            ? await allocationService.listPool(params, signal)
            : await allocationService.listUnallocated(params, signal);
        return toProfessionalOptions(response);
      },
      placeholderData: keepPreviousData,
    }),

  clients: (search: string) =>
    queryOptions({
      queryKey: [...allocationQueries.all(), 'clients', search] as const,
      queryFn: async ({ signal }) => {
        await sleepUnlessAborted(SEARCH_DEBOUNCE_MS, signal);
        return allocationService.searchClients(search, signal);
      },
      placeholderData: keepPreviousData,
    }),

  contracts: (search: string, params: ContractSearchParams = {}) =>
    queryOptions({
      queryKey: [
        ...allocationQueries.all(),
        'contracts',
        search,
        params.clientId,
        params.allocatable,
      ] as const,
      queryFn: async ({ signal }) => {
        await sleepUnlessAborted(SEARCH_DEBOUNCE_MS, signal);
        return allocationService.searchContracts(search, params, signal);
      },
      placeholderData: keepPreviousData,
    }),

  projects: (search: string, contractId?: number) =>
    queryOptions({
      queryKey: [...allocationQueries.all(), 'projects', search, contractId] as const,
      queryFn: async ({ signal }) => {
        await sleepUnlessAborted(SEARCH_DEBOUNCE_MS, signal);
        return allocationService.searchProjects(search, contractId, signal);
      },
      placeholderData: keepPreviousData,
    }),

  professionals: (search: string, multiAllocated?: boolean) =>
    queryOptions({
      queryKey: [...allocationQueries.all(), 'professionals', search, multiAllocated] as const,
      queryFn: async ({ signal }) => {
        await sleepUnlessAborted(SEARCH_DEBOUNCE_MS, signal);
        return allocationService.searchProfessionals(search, multiAllocated, signal);
      },
      placeholderData: keepPreviousData,
    }),

  tagOptions: () =>
    queryOptions({
      queryKey: [...allocationQueries.all(), 'tagOptions'] as const,
      queryFn: ({ signal }) => allocationService.listTagOptions(signal),
    }),

  tags: (search: string) =>
    queryOptions({
      queryKey: [...allocationQueries.all(), 'tags', search] as const,
      queryFn: async ({ signal }) => {
        await sleepUnlessAborted(SEARCH_DEBOUNCE_MS, signal);
        return allocationService.searchTags(search, signal);
      },
      placeholderData: keepPreviousData,
    }),

  salesProfiles: (contractId: number | undefined) =>
    queryOptions({
      queryKey: [...allocationQueries.all(), 'salesProfiles', contractId] as const,
      // biome-ignore lint/style/noNonNullAssertion: `enabled` garante o contrato
      queryFn: ({ signal }) => allocationService.listSalesProfiles(contractId!, signal),
      enabled: contractId !== undefined,
    }),

  /** Buscado sob demanda (`fetchQuery`) quando o profissional muda; o custo do contrato muda pouco. */
  defaults: (params: AllocationDefaultsParams) =>
    queryOptions({
      queryKey: [...allocationQueries.all(), 'defaults', params] as const,
      queryFn: ({ signal }) => allocationService.getDefaults(params, signal),
      staleTime: 60_000,
    }),

  validation: (body: (AllocationBody & { id?: number }) | null) =>
    queryOptions({
      queryKey: [...allocationQueries.all(), 'validation', body] as const,
      queryFn: async ({ signal }) => {
        await sleepUnlessAborted(VALIDATION_DEBOUNCE_MS, signal);
        // biome-ignore lint/style/noNonNullAssertion: `enabled: !!body` no hook garante isso
        return allocationService.validate(body!, signal);
      },
      enabled: !!body,
    }),
};
